// ── 대외비 비밀번호 창 (2026-10-16, 서버 confidential-lock.route.js) ─────────────
//   대외비 메뉴(급여 관리·연차정산·흐름)를 감싼다. 그 회사에 대외비 비밀번호가 있으면 화면 대신 이 창이 먼저 뜬다 — 운영자·관리자·직원 모두.
//   비밀번호는 회사마다 하나라, 한 번 풀면 그 회사 대외비 메뉴끼리는 옮겨 다녀도 열려 있다(사용자 결정 a).
//   다시 잠김: 대외비가 아닌 메뉴로 나가거나 새로고침(출입증이 메모리에만 있음) · 이 화면에서 10분 동안 아무것도 안 하면.
//   쓰는 동안에는 4분마다 출입증을 연장해 서버 쪽 10분도 넘기지 않는다. 서버가 423(잠김)을 주면 바로 이 창으로 돌아온다.
//   props: companyCode(잠금 기준 회사 — 흐름은 본사), menuName(화면 이름), menuKey(기록용: payroll·leave_settlement·cashflow)
import React, { useCallback, useEffect, useState } from 'react';
import { Lock } from 'lucide-react';
import { apiClient } from '../../../api/apiClient';
import {
  CONFIDENTIAL_LOCKED_EVENT, clearConfidentialUnlockToken, getConfidentialUnlockToken, holdConfidentialUnlock,
  setConfidentialUnlockToken,
} from '../../../api/confidentialUnlock';
import { Button } from '../../../components/ui/Button';
import { useCompanyNames } from '../adminlayout/useCompanyNames';

const TICK_MS = 30 * 1000;
const IDLE_TICKS = 20;      // 30초 × 20 = 10분
const KEEPALIVE_TICKS = 8;  // 30초 × 8 = 4분
const ACTIVITY_EVENTS = ['mousemove', 'mousedown', 'keydown', 'wheel', 'touchstart', 'scroll'];

const tokenHeaders = () => {
  const t = getConfidentialUnlockToken();
  return t ? { 'X-Confidential-Unlock': t } : {};
};
const statusUrl = (code) => `/api/admin/confidential-lock/status?company_code=${encodeURIComponent(code)}`;

export default function ConfidentialLockGate({ companyCode, menuName, menuKey, children }) {
  const code = String(companyCode || '').trim().toUpperCase();
  const nameOf = useCompanyNames();
  const [state, setState] = useState('checking'); // checking | locked | open
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);

  const lock = useCallback(() => {
    clearConfidentialUnlockToken();
    setPassword('');
    setState('locked');
  }, []);

  // 열려 있는 동안 출입증을 잡아 둔다 — 대외비 화면을 모두 떠나면 지워진다
  useEffect(() => holdConfidentialUnlock(), []);

  // 처음·회사가 바뀔 때 잠겼는지 묻는다. 묻지 못하면 열어 둔다 — 서버가 자료를 423 으로 막고 이 창이 다시 뜬다.
  useEffect(() => {
    let alive = true;
    setState('checking');
    setError('');
    (async () => {
      const res = await apiClient.get(statusUrl(code), { headers: tokenHeaders() });
      if (!alive) return;
      if (res.ok && res.data?.locked && !res.data?.unlocked) { setState('locked'); return; }
      if (res.ok && res.data?.token) setConfidentialUnlockToken(res.data.token);
      setState('open');
    })();
    return () => { alive = false; };
  }, [code]);

  // 서버가 잠겼다고 하면 바로 잠근다
  useEffect(() => {
    const onLocked = () => lock();
    window.addEventListener(CONFIDENTIAL_LOCKED_EVENT, onLocked);
    return () => window.removeEventListener(CONFIDENTIAL_LOCKED_EVENT, onLocked);
  }, [lock]);

  // 열려 있는 동안: 10분 동안 손대지 않으면 잠금, 손대는 중이면 4분마다 출입증 연장
  useEffect(() => {
    if (state !== 'open' || !getConfidentialUnlockToken()) return undefined;
    let idle = 0;
    let sinceKeepalive = 0;
    let touched = false;
    let active = false;
    const mark = () => { active = true; };
    ACTIVITY_EVENTS.forEach((ev) => window.addEventListener(ev, mark, { passive: true }));
    const timer = window.setInterval(async () => {
      if (active) { idle = 0; touched = true; active = false; } else { idle += 1; }
      if (idle >= IDLE_TICKS) { lock(); return; }
      sinceKeepalive += 1;
      if (sinceKeepalive >= KEEPALIVE_TICKS && touched) {
        sinceKeepalive = 0;
        touched = false;
        const res = await apiClient.get(statusUrl(code), { headers: tokenHeaders() });
        if (res.ok && res.data?.locked && !res.data?.unlocked) lock();
        else if (res.ok && res.data?.token && getConfidentialUnlockToken()) setConfidentialUnlockToken(res.data.token);
      }
    }, TICK_MS);
    return () => {
      window.clearInterval(timer);
      ACTIVITY_EVENTS.forEach((ev) => window.removeEventListener(ev, mark));
    };
  }, [state, code, lock]);

  async function submit(e) {
    e.preventDefault();
    if (busy) return;
    setBusy(true);
    setError('');
    const res = await apiClient.post('/api/admin/confidential-lock/unlock',
      { company_code: code, password, menu: menuKey || null }, { headers: tokenHeaders() });
    setBusy(false);
    setPassword('');
    if (!res.ok) { setError(res.data?.message || '비밀번호를 확인하지 못했습니다.'); return; }
    if (res.data?.token) setConfidentialUnlockToken(res.data.token);
    setState('open');
  }

  if (state === 'checking') {
    return <div className="py-16 text-center text-sm text-[var(--text-muted)]">확인 중…</div>;
  }
  if (state === 'open') return children;

  const companyName = nameOf(code);
  return (
    <div className="min-h-[calc(100vh-160px)] flex items-center justify-center px-4">
      <form
        onSubmit={submit}
        className="w-full max-w-[360px] rounded-xl border border-[var(--border)] bg-[var(--bg-card)] p-5 shadow-sm"
      >
        <div className="w-10 h-10 rounded-xl bg-accent-500/10 text-accent-600 dark:text-accent-400 flex items-center justify-center mb-4">
          <Lock size={20} />
        </div>
        <h2 className="text-lg font-extrabold text-[var(--text)]">{menuName}</h2>
        <p className="text-xs text-[var(--text-muted)] mt-1 mb-4">
          대외비 메뉴입니다. {companyName} 대외비 비밀번호를 입력해 주세요.
        </p>
        <input
          type="password"
          autoComplete="off"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          className="w-full h-10 px-3 rounded-lg border border-[var(--border)] bg-white dark:bg-pine-800 dark:[--autofill-bg:rgb(var(--tw-pine-800))] text-[var(--text)] text-sm focus:outline-none focus:border-accent-500"
          placeholder="대외비 비밀번호"
          aria-label="대외비 비밀번호"
          autoFocus
        />
        {error && <div className="mt-2 text-xs font-semibold text-red-600 dark:text-red-400">{error}</div>}
        <Button type="submit" variant="primary" size="sm" className="w-full mt-4" disabled={busy}>
          {busy ? '확인 중...' : '확인'}
        </Button>
        <p className="text-[11px] text-[var(--text-muted)] mt-3">
          한 번 풀면 {companyName}의 대외비 메뉴는 함께 열립니다. 다른 메뉴로 나가거나 10분 동안 쓰지 않으면 다시 잠깁니다.
        </p>
      </form>
    </div>
  );
}
